import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Sparkles } from 'lucide-react';
import { storiesApi } from '@/api/stories';
import { CreateStoryForm } from '@/types/database';

interface StoryCreatorProps {
  onStoryCreated?: (story: any) => void;
}

const StoryCreator: React.FC<StoryCreatorProps> = ({ onStoryCreated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [formData, setFormData] = useState<CreateStoryForm>({
    title: '',
    description: '',
    genre: 'adventure',
    age_group: '3-5',
    illustration_style: 'watercolor',
    story_length: 'medium',
    main_character: '',
    prompt: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await storiesApi.createStory(formData);
      
      if (result.success && result.data) {
        onStoryCreated?.(result.data);
        // Reset form
        setFormData({
          title: '',
          description: '',
          genre: 'adventure',
          age_group: '3-5',
          illustration_style: 'watercolor',
          story_length: 'medium',
          main_character: '',
          prompt: ''
        });
      } else {
        setError(result.error || 'Failed to create story');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreateStoryForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          Create AI Story
        </CardTitle>
        <CardDescription>
          Generate a personalized story for your child using AI
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Story Prompt */}
          <div className="space-y-2">
            <Label htmlFor="prompt">Story Prompt *</Label>
            <Textarea
              id="prompt"
              placeholder="Tell me about a magical adventure in a forest..."
              value={formData.prompt}
              onChange={(e) => handleInputChange('prompt', e.target.value)}
              required
              className="min-h-[100px]"
            />
          </div>

          {/* Main Character */}
          <div className="space-y-2">
            <Label htmlFor="main_character">Main Character</Label>
            <Input
              id="main_character"
              placeholder="e.g., Brave little Emma, Curious cat Whiskers"
              value={formData.main_character}
              onChange={(e) => handleInputChange('main_character', e.target.value)}
            />
          </div>

          {/* Story Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="genre">Genre</Label>
              <Select value={formData.genre} onValueChange={(value) => handleInputChange('genre', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adventure">Adventure</SelectItem>
                  <SelectItem value="fantasy">Fantasy</SelectItem>
                  <SelectItem value="educational">Educational</SelectItem>
                  <SelectItem value="bedtime">Bedtime</SelectItem>
                  <SelectItem value="friendship">Friendship</SelectItem>
                  <SelectItem value="nature">Nature</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="age_group">Age Group</Label>
              <Select value={formData.age_group} onValueChange={(value) => handleInputChange('age_group', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-2">1-2 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="6-8">6-8 years</SelectItem>
                  <SelectItem value="9-12">9-12 years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="story_length">Length</Label>
              <Select value={formData.story_length} onValueChange={(value) => handleInputChange('story_length', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short (3 pages)</SelectItem>
                  <SelectItem value="medium">Medium (5 pages)</SelectItem>
                  <SelectItem value="long">Long (8 pages)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Illustration Style */}
          <div className="space-y-2">
            <Label htmlFor="illustration_style">Illustration Style</Label>
            <Select value={formData.illustration_style} onValueChange={(value) => handleInputChange('illustration_style', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="watercolor">Watercolor</SelectItem>
                <SelectItem value="cartoon">Cartoon</SelectItem>
                <SelectItem value="realistic">Realistic</SelectItem>
                <SelectItem value="minimalist">Minimalist</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Add any additional details about the story..."
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button 
            type="submit" 
            disabled={loading || !formData.prompt.trim()}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Story & Images...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Story
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default StoryCreator; 